import { observable } from 'mobx';
import type { OrderItemJobTypeKey } from '@this/src/domain/order_item/order_item_job_type';

export type RakutenHotelCancelCommentType = 'normal' | 'warning' | 'important';

export const rakutenHotelCancelCommentTypeLabels: Record<RakutenHotelCancelCommentType, string> = {
  normal: '通常',
  warning: '要確認',
  important: '重要'
};

export type RakutenHotelCancelInventoryCommentResponseArgs = {
  id: number;
  content: string;
  status: RakutenHotelCancelCommentType;
  commented_by_name: string;
  commented_at: string;
};

export type RakutenHotelCancelInventoryCommentArgs = {
  id: number;
  content: string;
  status: RakutenHotelCancelCommentType;
  commentedByName: string;
  commentedAt: string;
};

const convertRakutenHotelCancelInventoryCommentResponseToArgs = (
  response: RakutenHotelCancelInventoryCommentResponseArgs
): RakutenHotelCancelInventoryCommentArgs => {
  return {
    id: response.id,
    content: response.content,
    status: response.status,
    commentedByName: response.commented_by_name,
    commentedAt: response.commented_at
  };
};

export type RakutenHotelCancelItemStatusResponseArgs = {
  order_item_id: number;
  trace_id: number;
  job_type: OrderItemJobTypeKey | null;
  status: number;
};

export type RakutenHotelCancelItemStatusArgs = {
  orderItemId: number;
  traceId: number;
  jobType: OrderItemJobTypeKey | null;
  status: number;
};

const convertRakutenHotelCancelItemStatusResponseToArgs = (
  response: RakutenHotelCancelItemStatusResponseArgs
): RakutenHotelCancelItemStatusArgs => {
  return {
    orderItemId: response.order_item_id,
    traceId: response.trace_id,
    jobType: response.job_type,
    status: response.status
  };
};

export type RakutenHotelCancelAmountsResponseArgs = {
  order_item: {
    hotel_price: number | null;
    cancel_charge: number | null;
    total: number;
  };
  rakuten: {
    original_amount_after_tax: number;
    amount_after_tax: number;
  };
};

export type RakutenHotelCancelAmountsArgs = {
  orderItem: {
    hotelPrice: number | null;
    cancelCharge: number | null;
    total: number;
  };
  rakuten: {
    originalAmountAfterTax: number;
    amountAfterTax: number;
  };
};

const convertRakutenHotelCancelAmountsResponseToArgs = (
  response: RakutenHotelCancelAmountsResponseArgs
): RakutenHotelCancelAmountsArgs => {
  return {
    orderItem: {
      hotelPrice: response.order_item.hotel_price,
      cancelCharge: response.order_item.cancel_charge,
      total: response.order_item.total
    },
    rakuten: {
      originalAmountAfterTax: response.rakuten.original_amount_after_tax,
      amountAfterTax: response.rakuten.amount_after_tax
    }
  };
};

export type RakutenHotelCancelInventoryItemResponseArgs = {
  id: number;
  trip_id: number;
  hotel_reservation_ids: string[];
  original_item: RakutenHotelCancelItemStatusResponseArgs | null;
  cancel_charge_item: RakutenHotelCancelItemStatusResponseArgs | null;
  api_canceled: boolean;
  hotel_name: string | null;
  checkin_date: string | null;
  checkout_date: string | null;
  amounts: RakutenHotelCancelAmountsResponseArgs;
  billed_at: string | null;
  requested_at: string;
  api_canceled_at: string | null;
  canceled_by_name: string | null;
  is_inventoried: boolean;
  inventoried_by: string | null;
  inventoried_at: string | null;
  last_error_message: string | null;
  comments: RakutenHotelCancelInventoryCommentResponseArgs[];
  api_uncanceled: boolean;
  amount_mismatched: boolean;
};

export type RakutenHotelCancelInventoryItemArgs = {
  id: number;
  tripId: number;
  hotelReservationIds: string[];
  originalItem: RakutenHotelCancelItemStatusArgs | null;
  cancelChargeItem: RakutenHotelCancelItemStatusArgs | null;
  apiCanceled: boolean;
  hotelName: string | null;
  checkinDate: string | null;
  checkoutDate: string | null;
  amounts: RakutenHotelCancelAmountsArgs;
  billedAt: string | null;
  requestedAt: string;
  apiCanceledAt: string | null;
  canceledByName: string | null;
  isInventoried: boolean;
  inventoriedBy: string | null;
  inventoriedAt: string | null;
  lastErrorMessage: string | null;
  comments: RakutenHotelCancelInventoryCommentArgs[];
  apiUncanceled: boolean;
  amountMismatched: boolean;
};

const convertRakutenHotelCancelInventoryItemResponseToArgs = (
  response: RakutenHotelCancelInventoryItemResponseArgs
): RakutenHotelCancelInventoryItemArgs => {
  return {
    id: response.id,
    tripId: response.trip_id,
    hotelReservationIds: response.hotel_reservation_ids,
    originalItem:
      response.original_item && convertRakutenHotelCancelItemStatusResponseToArgs(response.original_item),
    cancelChargeItem:
      response.cancel_charge_item &&
      convertRakutenHotelCancelItemStatusResponseToArgs(response.cancel_charge_item),
    apiCanceled: response.api_canceled,
    hotelName: response.hotel_name,
    checkinDate: response.checkin_date,
    checkoutDate: response.checkout_date,
    amounts: convertRakutenHotelCancelAmountsResponseToArgs(response.amounts),
    billedAt: response.billed_at,
    requestedAt: response.requested_at,
    apiCanceledAt: response.api_canceled_at,
    canceledByName: response.canceled_by_name,
    isInventoried: response.is_inventoried,
    inventoriedBy: response.inventoried_by,
    inventoriedAt: response.inventoried_at,
    lastErrorMessage: response.last_error_message,
    comments: response.comments.map(convertRakutenHotelCancelInventoryCommentResponseToArgs),
    apiUncanceled: response.api_uncanceled,
    amountMismatched: response.amount_mismatched
  };
};

export default class RakutenHotelCancelInventoryItem {
  @observable readonly id: number;
  @observable readonly tripId: number;
  @observable readonly hotelReservationIds: string[];
  @observable readonly originalItem: RakutenHotelCancelItemStatusArgs | null;
  @observable readonly cancelChargeItem: RakutenHotelCancelItemStatusArgs | null;
  @observable readonly apiCanceled: boolean;
  @observable readonly hotelName: string | null;
  @observable readonly checkinDate: string | null;
  @observable readonly checkoutDate: string | null;
  @observable readonly amounts: RakutenHotelCancelAmountsArgs;
  @observable readonly billedAt: string | null;
  @observable readonly requestedAt: string;
  @observable readonly apiCanceledAt: string | null;
  @observable readonly canceledByName: string | null;
  @observable readonly isInventoried: boolean;
  @observable readonly inventoriedBy: string | null;
  @observable readonly inventoriedAt: string | null;
  @observable readonly lastErrorMessage: string | null;
  @observable readonly comments: RakutenHotelCancelInventoryCommentArgs[];
  @observable readonly apiUncanceled: boolean;
  @observable readonly amountMismatched: boolean;

  constructor(args: RakutenHotelCancelInventoryItemArgs) {
    this.id = args.id;
    this.tripId = args.tripId;
    this.hotelReservationIds = args.hotelReservationIds;
    this.originalItem = args.originalItem;
    this.cancelChargeItem = args.cancelChargeItem;
    this.apiCanceled = args.apiCanceled;
    this.hotelName = args.hotelName;
    this.checkinDate = args.checkinDate;
    this.checkoutDate = args.checkoutDate;
    this.amounts = args.amounts;
    this.billedAt = args.billedAt;
    this.requestedAt = args.requestedAt;
    this.apiCanceledAt = args.apiCanceledAt;
    this.canceledByName = args.canceledByName;
    this.isInventoried = args.isInventoried;
    this.inventoriedBy = args.inventoriedBy;
    this.inventoriedAt = args.inventoriedAt;
    this.lastErrorMessage = args.lastErrorMessage;
    this.comments = args.comments;
    this.apiUncanceled = args.apiUncanceled;
    this.amountMismatched = args.amountMismatched;
  }

  static fromResponse(response: RakutenHotelCancelInventoryItemResponseArgs): RakutenHotelCancelInventoryItem {
    return new RakutenHotelCancelInventoryItem(convertRakutenHotelCancelInventoryItemResponseToArgs(response));
  }

  // canceled_by_name=NULL は自動実行を意味する（決定事項B: 「システム」表示）。未実行の行は実行者なし
  get canceledByLabel(): string | null {
    if (this.canceledByName) return this.canceledByName;
    return this.apiCanceledAt ? 'システム' : null;
  }

  // API失敗だけでなく、API成功後に会計計上を停止したケース（請求済みガード等）もlastErrorMessageで表面化する
  get needsAttention(): boolean {
    return this.apiUncanceled || !!this.lastErrorMessage;
  }

  // 要確認行（ピンク行）と金額不一致は一括棚卸し対象から外し、個別確認を強制する
  get bulkInventoriable(): boolean {
    return !this.isInventoried && !this.needsAttention && !this.amountMismatched;
  }
}
